import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslations } from "@acto/web-common/locales";
import { Meeting } from "@acto/taskpane/core/domain";
import type { Customer } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, useTrack } from "@acto/taskpane/core/analytics";
import { useSuccessModal } from "@acto/taskpane/field-sales-2/components/primitives/FullScreenSuccessModal";
import { useToast } from "@acto/taskpane/field-sales-2/components/primitives/Toast";
import { useCreateRecap, useRecapTranscription } from "@acto/taskpane/field-sales-2/data-access/recap";
import type { RecapNote } from "@acto/taskpane/field-sales-2/data-access/recap";

/** The screen the current meeting should show. Derived per-meeting, never stored. */
export type RecapScreen = "compose" | "animating" | "summary";

interface UseRecapFlowArgs {
  meetings: Meeting[];
  /**
   * When set (and `meetings` is empty) the flow runs in ad-hoc mode: a single recap for this
   * customer without a pre-existing meeting. On confirm the meeting is created together with
   * the note (`saveNote` with `adHocMeeting`), so the report counts as a Besuchsbericht.
   */
  adHocCustomer?: Customer;
  /** Visit date for the ad-hoc meeting (e.g. from mark-as-done). Defaults to today. */
  adHocSeed?: { scheduledDate?: string };
  onClose: () => void;
  /** Collapse the expanded card detail when entering writing (owned by the morph transition). */
  onEnterWriting: () => void;
  /** Reset the writing morph when advancing the batch. */
  onExitWriting: () => void;
}

/**
 * Ad-hoc recaps have no pre-existing meeting, but the flow's state and card deck are keyed on a
 * Meeting. We wrap the customer in a single synthetic Meeting whose client-generated id becomes
 * the real meeting id on commit (stable per session, so retries and edits upsert instead of
 * creating a second meeting).
 */
function toInitialItems(
  meetings: Meeting[],
  adHocCustomer?: Customer,
  adHocSeed?: UseRecapFlowArgs["adHocSeed"],
): Meeting[] {
  if (meetings.length > 0) return meetings;
  if (adHocCustomer) {
    const scheduledDate = adHocSeed?.scheduledDate ? dayjs(adHocSeed.scheduledDate) : dayjs();
    return [new Meeting(crypto.randomUUID(), adHocCustomer, scheduledDate, null)];
  }
  return [];
}

/**
 * The recap flow's state machine. The user cycles freely across the batch in both directions;
 * each meeting keeps its own compose draft and (once committed) its own recap result, so the
 * correct screen is derived per meeting. "Weiter" removes the current meeting from the stack.
 * Pure state + handlers — all presentation (deck rendering, the morph) stays in the component.
 */
export function useRecapFlow({
  meetings,
  adHocCustomer,
  adHocSeed,
  onClose,
  onEnterWriting,
  onExitWriting,
}: UseRecapFlowArgs) {
  const isAdHoc = meetings.length === 0 && adHocCustomer != null;
  const [items, setItems] = useState<Meeting[]>(() => toInitialItems(meetings, adHocCustomer, adHocSeed));
  // Analytics `mode` must reflect the batch the flow STARTED with — `items` shrinks on every
  // handleNext, so the live length would misclassify the last meeting of a batch as "single".
  const initialBatchSizeRef = useRef(items.length);
  const [index, setIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);

  // Per-meeting compose text, keyed by meeting id.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Per-meeting recap result (note + AI summary), keyed by meeting id.
  const [recaps, setRecaps] = useState<Record<string, { note: RecapNote; summaryText: string | null }>>({});
  // The meeting whose summarize is in flight — the transient "animating" state.
  const [committingId, setCommittingId] = useState<string | null>(null);
  const [pendingEditContent, setPendingEditContent] = useState<string | null>(null);

  // useCreateRecap owns all the async work: saving the note and running the summarize SSE
  // stream. This flow never touches the network itself — it only reads useCreateRecap's
  // status flags (isDone / isSummarizing / error) to persist the result once it completes.
  const recap = useCreateRecap();
  const track = useTrack();
  // Which meetings have ever used voice input during this flow. A meeting can stop and
  // re-record (e.g. pause to think, then continue), so this must survive across those
  // stop/start cycles rather than reflect only the in-flight recording state at confirm time.
  const voiceUsedMeetingIdsRef = useRef<Set<string>>(new Set());

  // Full-screen success overlay shown once the whole batch is done. Counts recaps the user
  // saved and acknowledged (each "Weiter"), so a batch that was entirely skipped shows nothing.
  const t = useTranslations("fieldSalesTwo.recaps");
  const { showSuccess } = useSuccessModal();
  const { showToast } = useToast();
  const completedCountRef = useRef(0);

  const closeBatch = useCallback(() => {
    if (completedCountRef.current > 0) {
      showSuccess({
        title: t("success.title"),
        subtitle: t("success.subtitle", { count: completedCountRef.current }),
        hint: t("success.hint"),
      });
    }
    onClose();
  }, [showSuccess, t, onClose]);

  // Voice transcription is a second way to write, not a phase: it appends to whatever is
  // already there. The text present when a session starts is frozen here, and the live
  // session transcript is spliced onto the end as it streams in.
  const transcription = useRecapTranscription();
  const frozenBaseRef = useRef("");

  const safeIndex = Math.min(index, Math.max(items.length - 1, 0));
  const meeting = items[safeIndex];
  const isMulti = items.length > 1;
  const name = meeting?.customer.name ?? "";

  // What the recap attaches to on commit: a to-be-created meeting for an ad-hoc recap (the
  // synthetic Meeting carries the client-generated id and visit date), the series occurrence
  // for a recurring meeting, else the single meeting.
  const recapTarget: {
    meetingId?: string;
    seriesId?: string;
    occurrenceDate?: string;
    adHocMeeting?: { id: string; scheduledDate: string };
  } = !meeting
    ? {}
    : isAdHoc
      ? {
          adHocMeeting: {
            id: meeting.id,
            scheduledDate: meeting.scheduledDate.format("YYYY-MM-DD"),
          },
        }
      : meeting.series && meeting.occurrenceDate
        ? { seriesId: meeting.series.id, occurrenceDate: meeting.occurrenceDate.format("YYYY-MM-DD") }
        : { meetingId: meeting.id };

  // The current meeting's compose draft.
  const transcript = (meeting && drafts[meeting.id]) ?? "";
  const setTranscript = (value: string) => {
    if (!meeting) return;
    setDrafts((d) => ({ ...d, [meeting.id]: value }));
  };

  // Splice the live transcription session onto the frozen base, routed into the current
  // meeting's draft so each meeting accumulates its own voice text.
  useEffect(() => {
    if (!meeting) return;
    const base = frozenBaseRef.current;
    const separator = base.trim().length > 0 ? " " : "";
    const next = transcription.sessionText ? base + separator + transcription.sessionText : base;
    setDrafts((d) => ({ ...d, [meeting.id]: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcription.sessionText]);

  const startRecording = () => {
    frozenBaseRef.current = transcript;
    if (meeting) voiceUsedMeetingIdsRef.current.add(meeting.id);
    transcription.start();
  };

  // Opening the flow should start at the top, not wherever the list was scrolled.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The current meeting's derived screen: animating while its summarize is in flight,
  // summary once a recap result exists for it, otherwise compose.
  const screen: RecapScreen =
    meeting && committingId === meeting.id ? "animating" : meeting && recaps[meeting.id] ? "summary" : "compose";

  const current = meeting ? recaps[meeting.id] : undefined;
  const summaryNote = current?.note ?? null;
  const summaryText = current?.summaryText ?? null;

  // Entering writing also collapses the expanded card detail.
  const handleEnterWriting = () => {
    setExpanded(false);
    onEnterWriting();
  };

  const cycle = (delta: number) => {
    if (items.length === 0) return;
    // A recording is tied to the meeting it started on; stop it before navigating so live
    // voice text can't splice into the next meeting's draft.
    transcription.stop();
    frozenBaseRef.current = "";
    // Leaving writing mode on navigation so the next meeting opens in its default
    // state (collapsed card + the cycler), not the writing-mode name pill.
    onExitWriting();
    setExpanded(false); // a freshly-fronted card starts collapsed
    setIndex((i) => (i + delta + items.length) % items.length);
  };

  const skip = () => {
    const willBeEmpty = items.length <= 1;
    setExpanded(false); // a freshly-fronted card starts collapsed, as with cycle
    setItems((prev) => prev.filter((_, i) => i !== safeIndex));
    setIndex((i) => Math.max(0, Math.min(i, items.length - 2)));
    if (willBeEmpty) closeBatch();
  };

  // A summarize failure still resolves forward, since the note is saved by then. The shared hook
  // is reset so the next commit starts clean.
  useEffect(() => {
    if (!committingId || !recap.note || (!recap.isDone && !recap.summarizeError)) return;
    const id = committingId;
    const note = recap.note;

    setRecaps((prev) => ({ ...prev, [id]: { note, summaryText: recap.summaryText } }));
    setCommittingId(null);
    setPendingEditContent(null);
    recap.reset();
    if (recap.summarizeError) {
      showToast({ message: t("animation.failed"), tone: "destructive" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committingId, recap.isDone, recap.summarizeError]);

  // The save's outcome is known here, the summary streams in afterwards and is stored by the
  // effect above. A failed save persisted nothing, so it returns to the screen that still holds
  // the text (compose, or the editor via `pendingEditContent`).
  const commitRecap = async (content: string): Promise<RecapNote | null> => {
    if (!meeting) return null;
    setCommittingId(meeting.id);
    const saved = await recap.commit({
      clientId: meeting.customer.id,
      ...recapTarget,
      content,
    });
    if (!saved) {
      setCommittingId(null);
      showToast({ message: t("edit.saveFailed"), tone: "destructive" });
    }
    return saved;
  };

  const handleConfirm = async () => {
    if (!meeting || transcription.recording || transcript.trim().length === 0) return;
    const saved = await commitRecap(transcript);
    if (!saved) return;
    track(AnalyticsEvents.NOTE_CREATED, {
      noteId: saved.id,
      customerId: meeting.customer.id,
      customerName: meeting.customer.name ?? "",
      contentLength: transcript.trim().length,
      note: transcript,
      noteType: "meeting_recap",
      wasVoiceRecorded: voiceUsedMeetingIdsRef.current.has(meeting.id),
      mode: initialBatchSizeRef.current > 1 ? "batch" : "single",
    });
  };

  // Editing the recap content from the summary screen: re-commit the note with the new content
  // (saveNote upserts by meetingId, so it overwrites) and re-trigger summarization. Reuses the
  // same in-flight/animation path as the initial commit — the completion effect above stores the
  // regenerated result and flips the screen back to summary. The AI summary and todos regenerate.
  const editRecapContent = async (content: string) => {
    if (!meeting || content.trim().length === 0) return;
    setPendingEditContent(content);
    await commitRecap(content);
  };

  // "Weiter" from the summary screen: drop the just-recapped meeting from the stack (reusing
  // skip's removal), tidy up its per-meeting state, tear down any recording, and advance.
  const handleNext = () => {
    const removedId = meeting?.id;
    const willBeEmpty = items.length <= 1;
    completedCountRef.current += 1;
    setItems((prev) => prev.filter((_, i) => i !== safeIndex));
    setIndex((i) => Math.max(0, Math.min(i, items.length - 2)));
    if (removedId) {
      setDrafts((d) => Object.fromEntries(Object.entries(d).filter(([k]) => k !== removedId)));
      setRecaps((r) => Object.fromEntries(Object.entries(r).filter(([k]) => k !== removedId)));
    }
    transcription.stop();
    frozenBaseRef.current = "";
    onExitWriting();
    if (willBeEmpty) closeBatch();
  };

  return {
    recap,
    items,
    safeIndex,
    meeting,
    isMulti,
    isAdHoc,
    name,
    expanded,
    setExpanded,
    screen,
    transcript,
    setTranscript,
    summaryNote,
    summaryText,
    recording: transcription.recording,
    elapsedSeconds: transcription.elapsedSeconds,
    startRecording,
    stopRecording: transcription.stop,
    handleEnterWriting,
    cycle,
    skip,
    handleConfirm,
    editRecapContent,
    pendingEditContent,
    discardPendingEdit: () => setPendingEditContent(null),
    handleNext,
  };
}
